import { useCallback, useEffect, useMemo, useState } from "react";
import { useCuttlefishConnection } from "../providers/CuttlefishProvider";

/**
 * Hook for accessing the singleton ADB connection from the device connection
 *
 * The ADB connection is automatically managed as a singleton on the device connection.
 * This hook provides a React-friendly interface with connection status and error handling.
 *
 * @returns Object with ADB connection, status, and control methods
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { adb, isConnected, executeShell } = useCuttlefishAdb()
 *
 *   const handleCommand = () => {
 *     executeShell('ls -la /sdcard')
 *   }
 *
 *   return (
 *     <div>
 *       <Text>ADB Status: {isConnected ? 'Connected' : 'Disconnected'}</Text>
 *       <Button onClick={handleCommand}>Run Command</Button>
 *     </div>
 *   )
 * }
 * ```
 */
export function useCuttlefishAdb() {
  const connection = useCuttlefishConnection();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adb = connection?.getAdb?.();

  // Poll connection status
  useEffect(() => {
    if (!adb) {
      setIsConnected(false);
      return;
    }

    // Check initial connection state
    setIsConnected(adb.isConnected);

    // Poll for connection state changes
    const interval = setInterval(() => {
      setIsConnected(adb.isConnected);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [adb, adb?.isConnected]);

  /**
   * Execute a shell command via ADB
   */
  const executeShell = useCallback(
    (command: string): number => {
      if (!adb) {
        const errorMsg = "ADB not available";
        console.error(errorMsg);
        setError(errorMsg);
        return -1;
      }

      try {
        const channelId = adb.adbShell(command);
        setError(null);
        return channelId;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "Failed to execute command";
        console.error("ADB shell error:", errorMsg);
        setError(errorMsg);
        return -1;
      }
    },
    [adb],
  );

  return useMemo(
    () => ({
      adb,
      isConnected,
      error,
      executeShell,
    }),
    [adb, isConnected, error, executeShell],
  );
}
